import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import { FaHeart, FaRegHeart, FaReply, FaEllipsisH } from 'react-icons/fa';

export default function ReplyItem({ reply, contentId, contentType, onReply }) {
    const auth = getAuth();
    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(reply.likes || 0);

    // Check if current user liked this reply
    useState(() => {
        if (auth.currentUser) {
            setIsLiked(reply.likedBy?.includes(auth.currentUser.uid) || false);
        }
    }, [auth.currentUser, reply.likedBy]);

    // Handle like/unlike
    const handleLike = async () => {
        if (!auth.currentUser) {
            toast.error("Please sign in to like replies");
            return;
        }

        try {
            const collectionPath = contentType === 'movie' 
                ? `movies/${contentId}/comments` 
                : `status/${contentId}/comments`;
            const replyRef = doc(db, collectionPath, reply.id);

            if (isLiked) {
                await updateDoc(replyRef, {
                    likes: likeCount - 1,
                    likedBy: arrayRemove(auth.currentUser.uid)
                });
                setLikeCount(likeCount - 1);
                setIsLiked(false);
            } else {
                await updateDoc(replyRef, {
                    likes: likeCount + 1,
                    likedBy: arrayUnion(auth.currentUser.uid)
                });
                setLikeCount(likeCount + 1);
                setIsLiked(true);
            }
        } catch (error) {
            console.error("Error updating like status:", error);
            toast.error("Failed to update like");
        }
    };

    // Format timestamp
    const formatTime = (timestamp) => {
        if (!timestamp?.toDate) return 'Just now';
        const now = new Date();
        const replyDate = timestamp.toDate();
        const diffInSeconds = Math.floor((now - replyDate) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
        return `${Math.floor(diffInSeconds / 86400)}d`;
    };

    return (
        <div className="reply-item">
            <style jsx>{`
                .reply-item {
                    margin-bottom: 12px;
                    padding-bottom: 12px;
                    border-bottom: 1px solid #3f3f46;
                }
                
                .reply-text {
                    word-break: break-word;
                }
                
                .reply-actions {
                    display: flex;
                    align-items: center;
                    margin-top: 8px;
                    gap: 16px;
                }
                
                .like-action {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    cursor: pointer;
                }
                
                .reply-action {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    cursor: pointer;
                }
            `}</style>

            <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                    <img 
                        src={reply.userPhoto || '/default-avatar.jpg'} 
                        alt={reply.userName}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                            e.target.src = '/default-avatar.jpg';
                        }}
                    />
                </div>
                <div className="flex-1">
                    <div className="flex items-center space-x-2">
                        <span className="font-semibold text-sm">{reply.userName}</span>
                        <span className="text-gray-400 text-xs">{formatTime(reply.timestamp)}</span>
                    </div>
                    <div className="mt-1">
                        <p className="text-sm text-white reply-text">
                            {reply.text}
                        </p>
                    </div>
                    
                    <div className="reply-actions">
                        <div className="like-action" onClick={handleLike}>
                            {isLiked ? (
                                <FaHeart className="text-red-500" />
                            ) : (
                                <FaRegHeart className="text-gray-400 hover:text-red-500" />
                            )}
                            <span className="text-xs">{likeCount}</span>
                        </div>
                        <div 
                            className="reply-action"
                            onClick={() => onReply(reply)}
                        >
                            <FaReply className="text-gray-400 hover:text-gray-200" />
                            <span className="text-xs">Reply</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}